import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from 'axios';
import { getRole, storeAccessToken, removeAccessToken } from 'service/AuthService';
import { roles as rolesConstant } from 'constants/constantText';
import { RouteList } from 'routes';

// Set the base URL for all API requests
axios.defaults.baseURL = process.env.REACT_APP_MARSHMALLOW_API;

/**
 * Interface for token credentials stored in localStorage
 */
interface IgetTokenCredentials {
  access_token: string | null;
  refresh_token: string | null;
}

/**
 * Interface for complete credentials with token type
 */
interface Credentials {
  access_token: string;
  token_type: string;
  refresh_token: string;
}

/**
 * Interface for failed request queue items during token refresh
 */
interface FailedQueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

/**
 * Extended Axios request config with retry flag
 */
interface InternalAxiosRequestConfig extends AxiosRequestConfig {
  retry?: boolean;
}

/**
 * Retrieves authentication tokens from localStorage
 * @returns Object containing access token, token type, and refresh token
 */
const getToken = (): { access_token: string | null; token_type: string; refresh_token: string | null; } => {
  const credentials: IgetTokenCredentials | null = JSON.parse(
    sessionStorage.getItem('credentials') as string,
  );
  const accessToken = credentials?.access_token ?? null;
  const refreshToken = credentials?.refresh_token ?? null;
  if ((accessToken != null) && (refreshToken != null)) {
    return {
      access_token: accessToken,
      token_type: 'bearer',
      refresh_token: refreshToken,
    };
  } else {
    return {
      access_token: null,
      token_type: 'bearer',
      refresh_token: null,
    };
  }
};

/**
 * API utility object containing methods for making HTTP requests
 */
const apiUtils = {
  /**
   * Makes a GET request without authentication token
   * @param uri - The API endpoint URI
   * @returns Promise resolving to the response data
   */
  getWithoutToken: async (uri: string) =>
    await new Promise((resolve, reject) => {
      axios
        .get(`api/${uri}`, {
          headers: {
            zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    }),

  /**
   * Downloads an Excel file with authentication token
   * @param uri - The API endpoint URI for file download
   * @returns Promise resolving to the file response
   */
  getExcelFile: async (uri: string) =>
    await new Promise((resolve, reject) => {
      const credentials = getToken() as Credentials;
      const route = getParentRoute();
      if (
        credentials?.access_token != null &&
        (credentials.token_type.length > 0) &&
        credentials.token_type.length > 0
      )
        axios
          .get(`api/${route}/${uri}`, {
            headers: {
              'x-access-token': credentials.access_token,
              'x-access-token-type': credentials.token_type,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            responseType: "arraybuffer"
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((error: AxiosError) => {
            reject(error);
          });
    }),

  /**
   * Makes a POST request without authentication token
   * @param uri - The API endpoint URI
   * @param body - The request body data (optional)
   * @returns Promise resolving to the response data
   */
  postWithoutToken: async (uri: string, body?: unknown) =>
    await new Promise((resolve, reject) => {
      axios
        .post(`api/${uri}`, body, {
          headers: {
            zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    }),

  postWithTempToken: async (uri: string, body?: unknown, token: string) =>
    await new Promise((resolve, reject) => {
      axios
        .post(`api/${uri}`, body, {
          headers: {
            'x-access-token': token,
            'x-access-token-type': 'bearer',
            zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    }),

  /**
   * Makes a GET request with authentication token
   * @param uri - The API endpoint URI
   * @returns Promise resolving to the response data
   */
  getWithToken: async (uri: string) =>
    await new Promise((resolve, reject) => {
      const credentials = getToken() as Credentials;
      const route = getParentRoute();

      if (
        credentials?.access_token != null &&
        (credentials.token_type.length > 0) &&
        credentials.token_type.length > 0
      ) {
        axios
          .get(`api/${route}/${uri}`, {
            headers: {
              'x-access-token': credentials.access_token,
              'x-access-token-type': credentials.token_type,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          })
          .then((response: AxiosResponse) => {
            resolve(response.data);
          })
          .catch((error: AxiosError) => {
            reject(error);
          });
      } else {
        reject(new Error('token is not valid'));
      }
    }),

  /**
   * Makes a POST request with authentication token
   * @param uri - The API endpoint URI
   * @param body - The request body data (optional)
   * @returns Promise resolving to the response data
   */
  postWithToken: async (uri: string, body?: unknown) =>
    await new Promise((resolve, reject) => {
      const credentials = getToken() as Credentials;
      const route = getParentRoute()
      if (
        credentials?.access_token != null &&
        credentials.token_type.length > 0
      ) {
        axios
          .post(`api/${route}/${uri}`, body, {
            headers: {
              'x-access-token': credentials.access_token,
              'x-access-token-type': credentials.token_type,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          })
          .then((response: AxiosResponse) => {
            resolve(response.data);
          })
          .catch((error: AxiosError) => {
            reject(error);
          });
      } else {
        reject(new Error('token is not valid'));
      }
    }),

  /**
   * Makes a PUT request with authentication token
   * @param uri - The API endpoint URI
   * @param body - The request body data
   * @returns Promise resolving to the response data
   */
  putWithToken: async (uri: string, body: unknown) =>
    await new Promise((resolve, reject) => {
      const credentials = getToken() as Credentials;
      const route = getParentRoute()

      if (
        credentials?.access_token != null &&
        credentials.token_type.length > 0
      ) {
        axios
          .put(`api/${route}/${uri}`, body, {
            headers: {
              'x-access-token': credentials.access_token,
              'x-access-token-type': credentials.token_type,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          })
          .then((response: AxiosResponse) => {
            resolve(response.data);
          })
          .catch((error: AxiosError) => {
            reject(error);
          });
      } else {
        reject(new Error('token is not valid'));
      }
    }),

  /**
   * Makes a PUT request without authentication token
   * @param uri - The API endpoint URI
   * @param body - The request body data (optional)
   * @returns Promise resolving to the response data
   */
  putWithoutToken: async (uri: string, body?: unknown) =>
    await new Promise((resolve, reject) => {
      axios
        .put(`api/${uri}`, body, {
          headers: {
            zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    }),

  /**
   * Makes a DELETE request with authentication token
   * @param uri - The API endpoint URI
   * @returns Promise resolving to the response data
   */
  deleteWithToken: async (uri: string) =>
    await new Promise((resolve, reject) => {
      const credentials = getToken() as Credentials;
      const route = getParentRoute()

      if (
        credentials?.access_token != null &&
        credentials.token_type.length > 0
      ) {
        axios
          .delete(`api/${route}/${uri}`, {
            headers: {
              'x-access-token': credentials.access_token,
              'x-access-token-type': credentials.token_type,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          })
          .then((response: AxiosResponse) => {
            resolve(response.data);
          })
          .catch((error: AxiosError) => {
            reject(error);
          });
      } else {
        reject(new Error('token is not valid'));
      }
    }),

  refreshToken: async () => await new Promise((resolve, reject) => {
    const credentials = getToken() as Credentials;
    axios
      .get('api/admin/auth/refreshToken', {
        headers: {
          'x-access-token': credentials.refresh_token,
          zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }).then(({ data }) => {
        void storeAccessToken(data);
        resolve(data);
      }).catch(error => {
        reject(error);
      })
  })

};

export default apiUtils;

// Global variables for token refresh mechanism
let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

/**
 * Processes the queue of failed requests during token refresh
 * @param error - The error that occurred during token refresh (null if successful)
 * @param token - The new token if refresh was successful (null if failed)
 */
const processQueue = (error: AxiosError | null, token: string | null = null): void => {
  failedQueue.forEach((prom: FailedQueueItem) => {
    if (error !== null && error !== undefined) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Axios response interceptor for handling token refresh
 * Automatically refreshes expired tokens and retries failed requests
 */
axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (err: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = err.config as InternalAxiosRequestConfig;

    if (err.response?.status === 403) {
      void removeAccessToken();
      sessionStorage.clear();
      window.history.replaceState(null, '', RouteList.SignIn);
      window.location.reload();
    }

    // Handle 401 Unauthorized errors (expired token)
    if (err.response?.status === 401 && !originalRequest?.retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(async (token: unknown) => {
            if (typeof token === 'string' && originalRequest?.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return await axios(originalRequest);
            }
            return await Promise.reject(new Error('originalRequest is undefined'));
          })
          .catch(async (e) => await Promise.reject(e));
      }

      // Mark request for retry and start refresh process
      if (originalRequest) {
        originalRequest.retry = true;
      }
      isRefreshing = true;

      return await new Promise((resolve, reject) => {
        const credentials = getToken() as Credentials;
        void axios
          .get('api/admin/auth/refreshToken', {
            headers: {
              'x-access-token': credentials.refresh_token,
              zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          })
          .then(({ data }) => {
            if (originalRequest?.headers) {
              originalRequest.headers['x-access-token'] = data.access_token;
              originalRequest.headers['x-access-token-type'] =
                credentials.token_type;
              void storeAccessToken(data);
              processQueue(null, data.fooToken);
              resolve(axios(originalRequest));
            } else {
              reject(new Error('originalRequest is undefined'));
            }
          })
          .catch((error: AxiosError) => {
            processQueue(error, null);
            window.location.reload();
            reject(error);
          })
          .then(() => {
            isRefreshing = false;
          });
      });
    }
    return await Promise.reject(err);
  },
);

/**
 * Determines the parent route based on user role
 * @returns The appropriate route prefix for API calls
 */
const getParentRoute = (): string => {
  const roles = getRole()
  if (roles && roles?.length > 0) {
    switch (roles[0]) {
      case rolesConstant.AuditorRole:
        return rolesConstant.AuditorRole
      case rolesConstant.OrganizationAdminRole:
        return rolesConstant.OrganizationAdminRole
      default:
        return 'admin';
    }
  }
  return ''
}